/**
 * ComplaintListPage — Issue #175.
 *
 * Admin queue for complaint / dispute work orders.
 *
 * 红线：
 *   - 只通过 useComplaints hook 与后端交互，不手写 fetch。
 *   - 所有 mutation 必须使用后端返回值刷新本地 state（不要乐观假状态）。
 *   - 升级/复盘等审计动作交由详情页处理，列表页仅展示。
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  COMPLAINT_PERMISSIONS,
  hasPermission,
} from '@/lib/complaintPermissions';
import {
  useComplaints,
  COMPLAINT_RISK_LABELS,
  COMPLAINT_STATE_LABELS,
  COMPLAINT_STATE_LIST,
  COMPLAINT_TYPE_LABELS,
  type ComplaintWorkOrderRiskLevel,
  type ComplaintWorkOrderStatus,
  type ComplaintWorkOrderType,
} from '@/hooks/useComplaints';

const STATUS_TONE: Record<ComplaintWorkOrderStatus, string> = {
  submitted: 'bg-slate-100 text-slate-700',
  accepted: 'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-800',
  escalated: 'bg-red-100 text-red-700',
  pending_review: 'bg-purple-100 text-purple-700',
  closed: 'bg-green-100 text-green-700',
  rejected: 'bg-slate-200 text-slate-600',
};

const RISK_TONE: Record<ComplaintWorkOrderRiskLevel, string> = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const BADGE_BASE = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium';

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function ComplaintListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLAINT_PERMISSIONS.READ);
  const canCreate = hasPermission(permissions, COMPLAINT_PERMISSIONS.CREATE);

  const { items, total, loading, error, fetchComplaints } = useComplaints();
  const [statusFilter, setStatusFilter] = useState<ComplaintWorkOrderStatus | ''>('');
  const [riskFilter, setRiskFilter] = useState<ComplaintWorkOrderRiskLevel | ''>('');
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchComplaints({
      status: statusFilter || undefined,
      riskLevel: riskFilter || undefined,
      escalated: escalatedOnly ? true : undefined,
      page,
      limit,
    });
  }, [canRead, statusFilter, riskFilter, escalatedOnly, page, fetchComplaints]);

  if (!canRead) {
    return <AccessDenied resource="投诉与纠纷工单" />;
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">投诉与纠纷工单</h1>
          <p className="text-sm text-slate-500">
            登记、受理、升级与复盘客户投诉。所有状态变更均通过后端状态机校验并写入审计事件。
          </p>
        </div>
        {canCreate ? (
          <Link
            href="/complaints/create"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            新建投诉工单
          </Link>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                e.target.value === ''
                  ? ''
                  : (e.target.value as ComplaintWorkOrderStatus),
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {COMPLAINT_STATE_LIST.map((s) => (
              <option key={s} value={s}>
                {COMPLAINT_STATE_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          风险等级
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={riskFilter}
            onChange={(e) => {
              setRiskFilter(
                e.target.value === ''
                  ? ''
                  : (e.target.value as ComplaintWorkOrderRiskLevel),
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(['low', 'medium', 'high', 'critical'] as const).map((r) => (
              <option key={r} value={r}>
                {COMPLAINT_RISK_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={escalatedOnly}
            onChange={(e) => {
              setEscalatedOnly(e.target.checked);
              setPage(1);
            }}
          />
          只看已升级
        </label>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">暂无投诉记录</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">标题</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">类型</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">状态</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">风险</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">客户 ID</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">处理人</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">SLA</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">已升级</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">创建时间</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3 font-medium">{row.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {COMPLAINT_TYPE_LABELS[row.complaintType as ComplaintWorkOrderType] ?? row.complaintType}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(BADGE_BASE, STATUS_TONE[row.status])}>
                      {COMPLAINT_STATE_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(BADGE_BASE, RISK_TONE[row.riskLevel])}>
                      {COMPLAINT_RISK_LABELS[row.riskLevel]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{row.customerId}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.assigneeId ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(row.slaDueAt)}</td>
                  <td className="px-4 py-3 text-xs">{row.escalated ? '是' : '否'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(row.createdAt)}</td>
                  <td className="px-4 py-3">
                    <Link href={`/complaints/${row.id}`} className="text-primary hover:underline">
                      查看
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            上一页
          </button>
          <span className="text-muted-foreground">
            {page} / {totalPages}（共 {total} 条）
          </span>
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </button>
        </div>
      ) : null}
    </div>
  );
}
